<template>
    <form ref="formElement" :class="finalClassName" @submit="onSubmit">
        <slot
            :fields="_controller.structure.properties"
            :data="_controller.value"
            :disabled="_controller.disabled"
            :valid="_controller.structure.valid"
        />

        <!-- Allow submission with Enter key -->
        <button type="submit" style="display: none" />
    </form>
</template>

<script lang="ts">
    import { toRaw } from 'vue';

    import { Component, Prop, Ref, Vue, Watch } from 'vue-property-decorator';

    import { FormController } from '@evidentid/json-schema/FormController';
    import JsonForm, { JsonFormType } from '@evidentid/json-schema/interfaces/JsonForm';

    import { Form } from '../Form';

    import { JsonSchemaFormStrings } from './types';

    @Component({
        components: { Form },
        emits: [ 'update:modelValue', 'submit' ],
    })
    export default class JsonSchemaCustomForm extends Vue {
        @Prop({ type: Object, default: null })
        private form!: JsonForm;

        @Prop({ type: Object, default: null })
        private controller!: FormController;

        @Prop({ type: String, default: '' })
        private className!: string;

        @Prop()
        private modelValue!: any;

        @Prop({ type: Boolean, default: false })
        private disabled!: boolean;

        @Prop({ type: Boolean, default: false })
        private touched!: boolean;

        @Prop({ type: Boolean, default: true })
        private scrollToErrors!: boolean;

        @Prop({ type: Object, default: () => ({}) })
        private strings!: Partial<JsonSchemaFormStrings>;

        @Ref()
        private formElement!: Form;

        private isMounted = false;
        // Set as teardown begins. Separate from `isMounted` because the value-change
        // subscription is installed by an `immediate` watcher and legitimately fires
        // before mount -- consumers such as EntityDetails rely on that first normalising
        // emission -- so only the far end may be suppressed.
        private isUnmounted = false;
        private _controller: FormController = this.controller || new FormController(this.form, this.modelValue);
        private unsubscribe: (() => void) | null = null;
        private unsubscribeDisabled: (() => void) | null = null;
        private forceUpdateScheduled = false;
        private emittingValueChange = false;

        public get $customJsonSchemaForm(): true {
            return true;
        }

        @Watch('form', { immediate: true })
        @Watch('controller')
        private updateController(): void {
            const baseForm = this.form || this.controller?.form;
            if (!baseForm) {
                throw new Error('You need to pass either "form" or "controller" for JsonSchemaCustomForm.');
            } else if (baseForm.type !== JsonFormType.object) {
                throw new Error('Only "object" forms are allowed for JsonSchemaCustomForm.');
            }
            this._controller = this.controller || new FormController(this.form, this.modelValue);
            this._controller.disabled = this.disabled;
            this._controller.touched = this.touched;
        }

        @Watch('_controller', { immediate: true })
        private subscribeToChanges(): void {
            this.unsubscribe?.();
            this.unsubscribe = this._controller?.onValueChange((value) => {
                // Parents two-way-bind `v-model="controller.value"`, so this emit lands
                // straight back on the controller's own setter. That write goes through
                // the parent's reactive proxy, which makes `this` inside the setter a
                // proxy of the controller: its `this[VALUE] === value` short-circuit
                // then compares a wrapped value against a raw one, never matches, and
                // emits another value change — recursing until the stack blows up.
                // Ignoring the echo while an emit is in flight ends the cycle; the
                // value being echoed is the one we just emitted, so nothing is lost.
                // The unmount check is not redundant with unsubscribing in
                // `beforeUnmount`: Subscriber.emit iterates a snapshot of its listeners
                // precisely so that unsubscribing mid-emit does not skip anyone, so a
                // listener earlier in that snapshot can tear this component down and
                // this one still gets called. Emitting then would reach a half-gone
                // instance, where `$emit` reads `emitsOptions` off a nulled internal
                // instance and throws, taking the whole page down rather than the form.
                if (this.emittingValueChange || this.isUnmounted) {
                    return;
                }
                this.emittingValueChange = true;
                try {
                    this.$emit('update:modelValue', value);
                } finally {
                    this.emittingValueChange = false;
                }
                this.scheduleForceUpdate();
            }) || null;
            this.unsubscribeDisabled?.();
            this.unsubscribeDisabled = this._controller?.onDisabledChange(() => {
                this.scheduleForceUpdate();
            }) || null;
        }

        // PRODUCT-26110 Phase 2B2: FormController fires onValueChange/onDisabledChange
        // synchronously from inside its reactive `set` trap, which can run while THIS
        // component's `@Watch('modelValue', { immediate: true }) updateCurrentValue` is
        // still initializing the controller — before Vue 3 has populated this instance's
        // render effect. Calling `$forceUpdate()` in that window queues a null scheduler
        // job and crashes ("Cannot read properties of null (reading 'flags')" in
        // queueJob). Defer to the next tick (coalesced) and skip if unmounted, so the
        // forced re-render always runs against a live instance.
        private scheduleForceUpdate(): void {
            if (this.forceUpdateScheduled) {
                return;
            }
            this.forceUpdateScheduled = true;
            this.$nextTick(() => {
                this.forceUpdateScheduled = false;
                if (this.isMounted) {
                    this.$forceUpdate();
                }
            });
        }

        private mounted(): void {
            this.isMounted = true;
        }

        // Drops the controller subscriptions at the first synchronous moment of
        // teardown. `unmounted` is queued as a post-flush job, so releasing them there
        // would leave this component subscribed for the rest of the patch and the whole
        // post-flush queue. Nothing in that span needs either subscription: the disabled
        // one only reaches `scheduleForceUpdate`, which checks `isMounted` anyway.
        private beforeUnmount(): void {
            this.isUnmounted = true;
            this.unsubscribe?.();
            this.unsubscribeDisabled?.();
            this.unsubscribe = null;
            this.unsubscribeDisabled = null;
        }

        private unmounted(): void {
            this.isMounted = false;
        }

        private get finalClassName(): string {
            return `JsonSchemaCustomForm ${this.className}`;
        }

        @Watch('modelValue', { immediate: true, deep: true })
        private updateCurrentValue(): void {
            // PRODUCT-26110 Phase 2B2: only push into the controller when the underlying
            // value actually differs. Under Vue 3 the incoming `modelValue` prop is a
            // reactive proxy, so FormController's identity guard (`this[VALUE] === value`)
            // never matches the raw value it already holds and it re-emits `onValueChange`.
            // Components that two-way-bind `v-model="controller.value"` (the criterion
            // editors) echo that straight back into the controller, so the sync loops until
            // Vue aborts with "Maximum recursive updates exceeded". Comparing raw targets
            // breaks the echo (mirrors the relyingParties race-guard fix, e67e6b0e6).
            // A caller that passes a controller and binds no model value has nothing to
            // push in -- the controller is the source of truth -- and pushing anyway
            // would write `undefined` over its value on mount and empty the form. This
            // asks whether the prop was bound rather than what it currently holds, so a
            // caller whose bound value legitimately becomes `undefined` or `null` (a
            // reset, say) still syncs.
            if (this.controller && !('modelValue' in (this.$.vnode.props ?? {}))) {
                return;
            }
            if (toRaw(this._controller.value) !== toRaw(this.modelValue)) {
                this._controller.value = this.modelValue;
            }
        }

        @Watch('disabled', { immediate: true })
        private updateDisabled(): void {
            this._controller.disabled = this.disabled;
        }

        private onSubmit(event: Event): void {
            event.preventDefault();
            if (!this.disabled) {
                this.touch();
                this.$emit('submit');
            }
        }

        private scrollToError(): void {
            if (!this.isMounted || !this.scrollToErrors) {
                return;
            }
            const element = this.formElement?.dom?.querySelector('.FormInput--error');
            if (!element) {
                return;
            }
            if (element.scrollIntoView) {
                element.scrollIntoView({ block: 'end' });
            }
            const innerElement = element.querySelector('input') as HTMLElement | null;
            if (innerElement) {
                innerElement.focus();
            }
        }

        public touch(): void {
            this._controller.touched = true;
        }

        /**
         * Component public method for the usage by ref too.
         */
        public scrollToFirstError(delay = true): void {
            if (delay) {
                requestAnimationFrame(() => this.scrollToError());
            } else {
                this.scrollToError();
            }
        }
    }
</script>
